/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useContext, useEffect, useState } from "react";
import { Column, GenericTable } from "../../components/GenericTable";
import { useMatchmakingService } from "../../services/backend/MatchmakingService";
import { Alert, AlertColor, MenuItem, Snackbar, TextField, Button, Paper } from "@mui/material";
import { AppContext } from "../../utils/AppContext";
import CodeMirror, { EditorView } from "@uiw/react-codemirror";
import { json } from "@codemirror/lang-json";
import { linter, Diagnostic } from "@codemirror/lint";
import { tfidHighlightExtension } from "utils/TfidCodemirrorExtension";
import { ResizableBox } from "react-resizable";
import "react-resizable/css/styles.css";

interface TicketType {
  id: string;
  status: string;
  players: string[];
  isBackfill: boolean;
  queueConfigurationId: string;
  startTime: number;
  endTime: number;
  matchId: string;
  tfid: string;
  failedReason: string;
}

const playerDataExample = '[ { "id": "4c9fj64s60g0e", "playerAttributes": { "playerId": "4c9fj64s60g0e" } } ]';

const validateJSON = (view: EditorView): Diagnostic[] => {
  const text = view.state.doc.toString();
  try {
    JSON.parse(text);
    return [];
  } catch (error: any) {
    const message = error.message;
    const positionMatch = message.match(/at position (\d+)/);
    const position = positionMatch ? parseInt(positionMatch[1], 10) : 0;

    return [
      {
        from: position,
        to: position + 1,
        severity: "error",
        message: message,
      },
    ];
  }
};

const TicketViewer = () => {
  const context = useContext(AppContext);

  const columns: Column<TicketType>[] = [
    { key: "id", label: "Ticket ID", type: "string", disableAdd: true },
    { key: "isBackfill", label: "Type", type: "string", render: (value) => (value ? "Backfill" : "Normal") },
    { key: "queueConfigurationId", label: "Queue Configuration ID", type: "string" },
    { key: "status", label: "Status", type: "string", disableAdd: true },
    {
      key: "startTime",
      label: "Start Date",
      type: "string",
      enabledSort: "desc",
      render: (value: TicketType[keyof TicketType]) =>
        typeof value === "number" ? new Date(value).toLocaleString() : "N/A",
      disableAdd: true,
    },
    {
      key: "endTime",
      label: "End Date",
      type: "string",
      render: (value: TicketType[keyof TicketType]) =>
        typeof value === "number" ? new Date(value).toLocaleString() : "N/A",
      disableAdd: true,
    },
    {
      key: "matchId",
      label: "Match ID",
      type: "string",
      disableAdd: true,
      render: (value: TicketType[keyof TicketType]) => (value ? value : "N/A"),
    },
    {
      key: "tfid",
      label: "TFID (Neighborhood, Expedition...)",
      type: "string",
      disableAdd: true,
      render: (value: TicketType[keyof TicketType]) => (value ? value : "N/A"),
    },
    {
      key: "failedReason",
      label: "Failed Reason",
      type: "string",
      disableAdd: true,
      render: (value: TicketType[keyof TicketType]) => (value ? value : "N/A"),
    },
  ];

  const matchmakingService = useMatchmakingService();

  const [ticketList, setTicketList] = useState<TicketType[]>([]);
  const [queueOptions, setQueueOptions] = useState<string[]>([]);
  const [newTicket, setNewTicket] = useState<Partial<TicketType>>({});
  const [ticketJSON, setTicketJSON] = useState<string>(playerDataExample);
  const [formWidth, setFormWidth] = useState(400); // Initial width of the form

  // Alerts
  const [alertMessage, setAlertMessage] = useState<string | undefined>(undefined);
  const [alertSeverity, setAlertSeverity] = useState<AlertColor>("info");

  const formatJSON = () => {
    try {
      const parsed = JSON.parse(ticketJSON);
      const formatted = JSON.stringify(parsed, null, 2); // Prettify JSON with 2 spaces
      setTicketJSON(formatted);
    } catch (_) {
      alert("Invalid JSON format");
    }
  };

  const fetchTicketList = async () => {
    try {
      const { tickets } = await matchmakingService.listTickets();
      setTicketList(
        tickets.map((ticket: TicketType, index: number) => ({
          ...ticket,
          id: ticket.id || String(index),
        })),
      );
    } catch (e: any) {
      setAlertMessage(e.debugMessage || "Could not fetch the list of tickets.");
      setAlertSeverity("error");
    }
  };

  const fetchQueueOptions = async () => {
    try {
      const { queues } = await matchmakingService.listQueues();
      setQueueOptions(queues.map((queue: { id: string }) => queue.id));
    } catch (e: any) {
      setAlertMessage(e.debugMessage || "Could not fetch the list of queues.");
      setAlertSeverity("error");
    }
  };

  const handleAddRow = async () => {
    try {
      const parsedTicket = JSON.parse(ticketJSON);

      const addedTicket = await matchmakingService.createTicket({ ...newTicket, players: parsedTicket });

      setTicketList((prevList) => [...prevList, addedTicket]);
      setNewTicket({});
      setTicketJSON(playerDataExample);
      formatJSON();
    } catch (e: any) {
      setAlertMessage(e.debugMessage || "Could not add the ticket.");
      setAlertSeverity("error");
    }
  };

  const handleInputChange = (key: keyof TicketType, value: any) => {
    setNewTicket((prev) => ({ ...prev, [key]: value }));
  };

  const alertCloseHandler = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === "clickaway") {
      return;
    }
    setAlertMessage(undefined);
  };

  const handleResize = (event: any, { size }: { size: { width: number; height: number } }) => {
    setFormWidth(size.width);
  };

  useEffect(() => {
    context?.setTitle("Ticket Viewer");
    context?.setTargetAPI("environment");

    let intervalId: NodeJS.Timeout | undefined;

    if (context?.selectedEnvironment) {
      (async () => {
        await fetchTicketList();
        await fetchQueueOptions();
      })();

      intervalId = setInterval(fetchTicketList, 5000);
    }

    formatJSON();

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [context?.selectedEnvironment]);

  return (
    <div style={{ display: "flex", height: "100%", gap: "16px" }}>
      {/* Resizable Form */}
      <ResizableBox
        width={formWidth}
        height={Infinity}
        axis="x"
        resizeHandles={["e"]}
        minConstraints={[400, Infinity]}
        maxConstraints={[800, Infinity]}
        handleSize={[20, 20]}
        lockAspectRatio={false}
        transformScale={1}
        onResize={handleResize}
      >
        <Paper sx={{ padding: 2, height: "100%" }}>
          <h3>Add New Ticket</h3>
          <TextField
            select
            label="Queue Configuration ID"
            value={newTicket.queueConfigurationId || ""}
            onChange={(e) => handleInputChange("queueConfigurationId", e.target.value)}
            fullWidth
            margin="normal"
          >
            {queueOptions.map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label="Type"
            value={newTicket.isBackfill || ""}
            onChange={(e) => handleInputChange("isBackfill", e.target.value)}
            fullWidth
            margin="normal"
          >
            {["Normal"].map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </TextField>
          <label>Players Data</label>
          <CodeMirror
            value={ticketJSON}
            height="200px"
            extensions={[json(), linter(validateJSON), tfidHighlightExtension]}
            onChange={(value) => setTicketJSON(value)}
          />
          <Button variant="outlined" color="secondary" onClick={formatJSON} fullWidth sx={{ marginTop: 1 }}>
            Format JSON
          </Button>
          <Button variant="contained" color="primary" onClick={handleAddRow} fullWidth sx={{ marginTop: 2 }}>
            Add Ticket
          </Button>
        </Paper>
      </ResizableBox>

      {/* GenericTable */}
      <div style={{ flex: 1, overflow: "auto" }}>
        <GenericTable rows={ticketList} columns={columns} enablePagination={true} enableSorting={true} />
      </div>

      {/* Snackbar for Alerts */}
      <Snackbar open={alertMessage !== undefined} autoHideDuration={5000} onClose={alertCloseHandler}>
        <Alert onClose={alertCloseHandler} severity={alertSeverity} sx={{ width: "100%" }}>
          {alertMessage}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default TicketViewer;
